import { useMemo, useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { googlecode } from 'react-code-blocks';
import { Virtuoso } from 'react-virtuoso';
import PropTypes from 'prop-types';

/**
 * Syntax highlighting emits a span per token, so an 800KB payload mounts upwards of 190,000 DOM
 * nodes and wedges the tab. Payloads past this many characters render as virtualized plain text.
 * Tracer payloads cluster at either a few hundred characters or several hundred KB, so the exact
 * cutoff only has to land in the gap between the two.
 */
export const HIGHLIGHT_CHARACTER_LIMIT = 50000;

export const isOversizedPayload = (text) => (text?.length ?? 0) > HIGHLIGHT_CHARACTER_LIMIT;

const LINE_HEIGHT = 18;

const LargePayloadBlock = ({ text, height = '700px', label = 'payload' }) => {
  const [copied, setCopied] = useState(false);
  const lines = useMemo(() => text.split('\n'), [text]);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    });
  };

  const gutterWidth = `${String(lines.length).length + 1}ch`;

  return (
    <Box width="100%" mx="0.75rem" borderRadius="0.25rem" overflow="hidden" bg={googlecode.backgroundColor}>
      <Flex align="center" justify="space-between" px={3} py={2} borderBottom="1px solid" borderColor="gray.200">
        <Text fontSize="xs" color="gray.600">
          {lines.length.toLocaleString()} lines, {Math.round(text.length / 1024).toLocaleString()} KB — syntax highlighting is off at this size
        </Text>
        <Button size="xs" variant="outline" onClick={handleCopy} aria-label={`copy ${label}`}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Flex>
      <Virtuoso
        data={lines}
        style={{ height, width: '100%' }}
        defaultItemHeight={LINE_HEIGHT}
        data-testid="large-payload-lines"
        itemContent={(index, line) => (
          <Flex fontFamily="Fira Code, monospace" fontSize="0.75rem" lineHeight={`${LINE_HEIGHT}px`} px={3}>
            <Box as="span" width={gutterWidth} flexShrink={0} color="gray.400" textAlign="right" pr={2} userSelect="none">
              {index + 1}
            </Box>
            <Box as="span" whiteSpace="pre-wrap" wordBreak="break-all" color={googlecode.color}>
              {line}
            </Box>
          </Flex>
        )}
      />
    </Box>
  );
};

LargePayloadBlock.propTypes = {
  text: PropTypes.string.isRequired,
  height: PropTypes.string,
  label: PropTypes.string,
};

export default LargePayloadBlock;
